import { IUploadFile } from "@/twapp/server/FileEndpoint";
import { getAllFiles, IContainer, IFile, IFileRoot, isFolder } from "../model/fileModel";
import { FileValidationResult, validateFilePath } from "../model/fileValidation";
import _ from "lodash";

function createUpload(root: IFileRoot, pathPrefix: string, file: File) {
    return {
        File: file,
        FilePath: pathPrefix + (file.webkitRelativePath || file.name),
        OwnerId: root.ownerId,
        OwnerType: root.ownerType,
        Replace: false,
    }
}

export interface IExistingFile {
    replaceCmd: IUploadFile;
    existing: IFile;
}

export interface IInvalidFile {
    path: string,
    error: FileValidationResult,
}

export interface IDuplicateFiles {
    path: string;
    files: IUploadFile[];
}

export interface IPreparedUpload {
    valid: IUploadFile[];
    existing: IExistingFile[];
    invalid: IInvalidFile[];
    hasErrors: boolean;
}

export function prepareFileUpload(root: IFileRoot, target: IContainer, inputFiles: Iterable<File>, validExtensions: string[]) {
    const result: IPreparedUpload = {
        existing: [],
        valid: [],
        invalid: [],
        hasErrors: false,
    }

    const pathPrefix = isFolder(target) ? target.fullPath + '/' : '';
    const extensions = new Set(_.map(validExtensions, x => x.toLowerCase()));
    const existingFiles = new Map(_.map(getAllFiles(target), x => [x.fullPath.toLowerCase(), x]));
    const uploads = _.map([...inputFiles], x => createUpload(root, pathPrefix, x));
    const uploadsByLcPath = _.groupBy(uploads, x => x.FilePath.toLowerCase());
    const [duplicates, uniques] = _.partition(uploadsByLcPath, x => x.length > 1);

    result.invalid = _.flatMap(duplicates, g => _.map(g, x => ({ path: x.FilePath, error: FileValidationResult.Duplicate })));

    for (let file of _.flatten(uniques)) {
        if (file.File.size > 100_000_000) {
            result.invalid.push({
                error: FileValidationResult.TooLarge,
                path: file.FilePath,
            });
            continue;
        }

        const validationResult = validateFilePath(file.FilePath, extensions);
        if (validationResult !== FileValidationResult.Valid) {
            result.invalid.push({
                error: validationResult,
                path: file.FilePath,
            });
            continue;
        }
        
        const existingFile = existingFiles.get(file.FilePath.toLowerCase());
        if (existingFile) {
            result.existing.push({
                replaceCmd: {
                    File: file.File,
                    OwnerId: file.OwnerId,
                    OwnerType: file.OwnerType,
                    FilePath: file.FilePath,
                    Replace: true,
                },
                existing: existingFile,
            });
            continue;
        }

        result.valid.push(file);
    }

    result.hasErrors = result.invalid.length > 0 || result.existing.length > 0;

    return result;
}
